import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Chip,
  Button,
  IconButton,
  CircularProgress,
  useMediaQuery,
  useTheme,
  makeStyles,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import BusinessIcon from '@material-ui/icons/Business';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import WarningIcon from '@material-ui/icons/Warning';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 20,
    padding: theme.spacing(1),
    maxWidth: 680,
    width: '100%',
  },
  companyCard: {
    borderRadius: 14,
    border: '1.5px solid #E2E8F0',
    transition: 'all 0.2s ease-in-out',
    boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
    '&:hover': {
      borderColor: '#17497F',
      boxShadow: '0 8px 24px rgba(23, 73, 127, 0.12)',
      transform: 'translateY(-2px)',
    },
  },
  companyCardDisabled: {
    opacity: 0.7,
  },
  avatarCircle: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: '#EEF4FF',
    color: '#17497F',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 14,
    flexShrink: 0,
  },
}));

const formatCpfCnpj = (val) => {
  if (!val) return '';
  const clean = val.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  if (clean.length === 14) {
    return clean.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }
  if (clean.length === 11) {
    return clean.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  return val;
};

const getStatusBadge = (company) => {
  if (company.is_trial) {
    return (
      <Chip
        size="small"
        icon={<AccessTimeIcon style={{ fontSize: 14, color: '#1D4ED8' }} />}
        label={
          company.days_remaining > 0
            ? `Teste Grátis (${company.days_remaining}d)`
            : 'Teste Expirado'
        }
        style={{
          backgroundColor: '#DBEAFE',
          color: '#1D4ED8',
          fontWeight: 700,
          fontSize: '0.72rem',
        }}
      />
    );
  }

  if (company.is_license_expired) {
    return (
      <Chip
        size="small"
        icon={<WarningIcon style={{ fontSize: 14, color: '#B45309' }} />}
        label="Carência / Pagamento Pendente"
        style={{
          backgroundColor: '#FEF3C7',
          color: '#B45309',
          fontWeight: 700,
          fontSize: '0.72rem',
        }}
      />
    );
  }

  if (company.status === 'active') {
    return (
      <Chip
        size="small"
        icon={<CheckCircleIcon style={{ fontSize: 14, color: '#15803D' }} />}
        label="Ativa"
        style={{
          backgroundColor: '#DCFCE7',
          color: '#15803D',
          fontWeight: 700,
          fontSize: '0.72rem',
        }}
      />
    );
  }

  if (company.status === 'pending_payment') {
    return (
      <Chip
        size="small"
        icon={<ErrorOutlineIcon style={{ fontSize: 14, color: '#C2410C' }} />}
        label="Aguardando Pagamento"
        style={{
          backgroundColor: '#FFEDD5',
          color: '#C2410C',
          fontWeight: 700,
          fontSize: '0.72rem',
        }}
      />
    );
  }

  return (
    <Chip
      size="small"
      label={company.status || 'Pendente'}
      style={{
        backgroundColor: '#F1F5F9',
        color: '#475569',
        fontWeight: 700,
        fontSize: '0.72rem',
      }}
    />
  );
};

export default function CompanySelectionModal({
  open,
  onClose,
  companies = [],
  onSelectCompany,
  onCreateNewCompany,
  loading = false,
  selectedCompanyId = null,
}) {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const validCompanies = (companies || []).filter((c) => Boolean(c.production_company_id || c.has_production));

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        className: classes.dialogPaper,
      }}
    >
      <DialogTitle style={{ paddingBottom: 8 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h6" style={{ fontWeight: 800, color: '#0F172A', lineHeight: 1.2 }}>
              Selecione a Empresa
            </Typography>
            <Typography variant="body2" style={{ color: '#64748B', marginTop: 4 }}>
              Você possui mais de um negócio cadastrado. Escolha qual deseja acessar:
            </Typography>
          </Box>
          <IconButton onClick={onClose} disabled={loading} size="small" style={{ marginTop: -4, marginRight: -4 }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent style={{ paddingTop: 8, paddingBottom: 16 }}>
        <Grid container spacing={2}>
          {validCompanies.map((company) => {
            const isSelected = selectedCompanyId === company.id;
            const isProcessing = loading && isSelected;

            return (
              <Grid item xs={12} key={company.id}>
                <Card className={`${classes.companyCard} ${loading && !isSelected ? classes.companyCardDisabled : ''}`}>
                  <CardActionArea
                    onClick={() => onSelectCompany && onSelectCompany(company)}
                    disabled={loading}
                    style={{ padding: '14px 16px' }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box display="flex" alignItems="center" style={{ minWidth: 0, flex: 1, paddingRight: 8 }}>
                        <div className={classes.avatarCircle}>
                          <BusinessIcon style={{ fontSize: 26 }} />
                        </div>
                        <Box style={{ minWidth: 0, flex: 1 }}>
                          <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={6} mb={0.5}>
                            <Typography
                              variant="subtitle1"
                              noWrap
                              style={{ fontWeight: 800, color: '#1E293B', fontSize: '0.98rem' }}
                            >
                              {company.name || company.pseudonym || 'Empresa sem nome'}
                            </Typography>
                            {getStatusBadge(company)}
                          </Box>

                          <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={12}>
                            {company.cpf_cnpj && (
                              <Typography variant="caption" style={{ color: '#64748B', fontWeight: 600 }}>
                                {formatCpfCnpj(company.cpf_cnpj)}
                              </Typography>
                            )}
                            {company.segment && (
                              <Typography variant="caption" style={{ color: '#64748B' }}>
                                • {company.segment}
                              </Typography>
                            )}
                            {company.production_company_id && (
                              <Typography
                                variant="caption"
                                style={{
                                  color: '#17497F',
                                  fontWeight: 700,
                                  backgroundColor: '#F0F7FF',
                                  padding: '2px 6px',
                                  borderRadius: 4,
                                }}
                              >
                                ID: #{company.production_company_id}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Box>

                      <Box display="flex" alignItems="center" justifyContent="center" pl={1}>
                        {isProcessing ? (
                          <CircularProgress size={22} color="primary" />
                        ) : (
                          <ArrowForwardIosIcon style={{ fontSize: 16, color: '#94A3B8' }} />
                        )}
                      </Box>
                    </Box>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>

      <DialogActions style={{ padding: '12px 16px', justifyContent: 'space-between', borderTop: '1px solid #F1F5F9' }}>
        <Button
          startIcon={<AddCircleOutlineIcon />}
          onClick={onCreateNewCompany}
          disabled={loading}
          style={{
            textTransform: 'none',
            fontWeight: 700,
            color: '#17497F',
            borderRadius: 8,
          }}
        >
          Criar Nova Empresa
        </Button>

        <Button
          onClick={onClose}
          disabled={loading}
          style={{
            textTransform: 'none',
            fontWeight: 600,
            color: '#64748B',
          }}
        >
          Cancelar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
